import styled from '@emotion/styled';
import {
  duration,
  easing,
  layout,
  palette,
  radius,
  shadow,
  spacing,
  typography,
} from '../../../style-utils/theme';
import { font } from '../../../style-utils/text';
import { down, touch } from '../../../style-utils/media-queries';
import { InlineMessage, SCALE, m } from '../../../motion';
import { StatusPill } from '../../status-pill/status-pill';
import { InfoBanner } from '../../onboarding/onboarding.styled';

/**
 * THE PORTAL KIT — the list, card and fact pieces every portal tab is built
 * from.
 *
 * Moved here unchanged from the single-page portal (tenant-dashboard.styled)
 * when it became a shell with tabs, so Home, Visitors and Parcels — and the
 * pass screens that come after them — share one row, one card and one fact
 * rather than growing a copy each. Two changes only, both marked where they
 * are: ListRow is a motion element now (rows appended by "Show more" arrive
 * with the list cascade), and the local status chip is gone because StatusPill
 * replaced it.
 */

export const Section = styled.section`
  margin-bottom: ${spacing.lg};
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.xs};
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-family: ${font.family};
  font-size: 16px;
  font-weight: 700;
  color: ${palette.ink};
`;

export const SectionHint = styled.span`
  font-family: ${font.family};
  font-size: ${font.size.xxs}px;
  color: ${palette.slateLight};
`;

export const Card = styled.div`
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  overflow: hidden;
`;

/* A MOTION ELEMENT, and that is the only change from the page it came from:
   the rows a "Show more" appends arrive with the capped list cascade, spread
   on by the caller with listItemMotion. The CSS is exactly as it was. */
export const ListRow = m.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.md};

  & + & {
    border-top: 1px solid ${palette.line};
  }
`;

export const RowMain = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

export const RowTitle = styled.div`
  font-family: ${font.family};
  font-size: ${font.size.xs}px;
  font-weight: 600;
  color: ${palette.ink};
`;

export const RowMeta = styled.div`
  font-family: ${font.family};
  font-size: ${font.size.xxs}px;
  color: ${palette.slate};
  margin-top: ${spacing.half};
`;

export const EmptyState = styled.div`
  padding: ${spacing.lg};
  color: ${palette.slate};
  font-family: ${font.family};
  font-size: ${font.size.xs}px;
  text-align: center;
`;

export const HomeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.md};
  padding: ${spacing.md};

  /* down('sm'), not a hand-written 520px: 520 is between the sm and md tokens,
     so this grid used to reflow at a width nothing else in the app responds to. */
  ${down('sm')} {
    grid-template-columns: 1fr;
  }
`;

export const Fact = styled.div``;

export const FactLabel = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-family: ${font.family};
  font-size: ${font.size.xxs}px;
  font-weight: 600;
  color: ${palette.slateLight};
  margin-bottom: 4px;
`;

export const FactValue = styled.div`
  font-family: ${font.family};
  font-size: ${font.size.s}px;
  font-weight: 600;
  color: ${palette.ink};
`;

export const FactMeta = styled.div`
  font-family: ${font.family};
  font-size: ${font.size.xxs}px;
  color: ${palette.slate};
  margin-top: ${spacing.half};
`;

/* ── Added with the shell ─────────────────────────────────────────────────── */

/**
 * THE STATUS ON A ROW. StatusPill, centred against the row the way the old
 * chip was — the pill's own default is the top of its line, which in a row
 * whose text wraps to two lines leaves the state floating above the middle.
 */
export const RowPill = styled(StatusPill)`
  flex: 0 0 auto;
  align-self: center;
`;

/** One home of several — each its own Building + Unit pair, ruled apart. */
export const HomeGroup = styled.div`
  & + & {
    border-top: 1px solid ${palette.line};
  }
`;

/**
 * THE HOME HEADLINE, moved from the single-page portal. One sentence, sized like
 * it matters, because it is the reason a resident opens the portal at all: is
 * there a parcel waiting.
 */
export const Greeting = styled.h1`
  margin: 0 0 ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxl}px;
  font-weight: ${typography.weight.bold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
`;

export const Sub = styled.p`
  margin: 0 0 ${spacing.lg};
  font-family: ${typography.ui};
  color: ${palette.slate};
`;

export const Headline = styled.section<{ $quiet?: boolean }>`
  padding: ${spacing.lg};
  margin-bottom: ${spacing.lg};
  border-radius: ${radius.md};
  border: 1px solid ${({ $quiet }) => ($quiet ? palette.line : palette.iris)};
  background: ${({ $quiet }) => ($quiet ? palette.surface : palette.irisTint)};
  box-shadow: ${shadow.sm};

  ${down('sm')} {
    padding: ${spacing.md};
  }
`;

export const HeadlineText = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
`;

export const HeadlineNote = styled.p`
  margin: ${spacing.half} 0 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

/**
 * A TAB'S OWN TITLE — the one h1 on Visitors and on Parcels. Smaller than the
 * Home greeting: the tab strip already says where you are, this names what the
 * page holds.
 */
export const TabTitle = styled.h1`
  margin: 0 0 ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.bold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
`;

export const TabLead = styled.p`
  margin: 0 0 ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

/* "Show more" and how far through the list you are, under the card. */
export const ListFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs} ${spacing.sm};
  margin-top: ${spacing.sm};
  min-height: ${layout.touchTarget};
`;

export const PageNote = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};

  &:focus {
    outline: none;
  }
`;

/* ── Added with live refresh and the push tip ─────────────────────────────── */

/**
 * THE PUSH TIP on Home: the onboarding InfoBanner in its brand tone, so a tip
 * looks like the tips a resident already met while signing up. Spaced like the
 * headline above it, so the two read as one column.
 */
export const TipCard = styled(InfoBanner)`
  gap: ${spacing.xs};
  margin: 0 0 ${spacing.lg};
`;

export const TipHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing.xs};
`;

export const TipTitle = styled.h2`
  margin: 0;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.bold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
`;

export const TipText = styled.p`
  margin: 0;
`;

export const TipActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  margin-top: ${spacing.half};
`;

/**
 * PUTTING THE TIP AWAY. Deliberately not the exit desk's BannerDismiss: that
 * one is a 24px square, sized for a mouse at a desk, and this card lives on a
 * resident's phone. A compact square under a mouse, the full touch target under
 * a thumb; the negative margin pulls the bigger box into the card's own padding
 * so it does not push the title down.
 * NO BACKTICKS IN THIS COMMENT: it sits inside a styled template literal.
 */
export const TipDismiss = styled.button`
  -webkit-appearance: none;
  appearance: none;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${layout.compactControlHeight};
  height: ${layout.compactControlHeight};
  margin: -${spacing.half} -${spacing.half} 0 0;
  padding: 0;
  border: none;
  border-radius: ${radius.xs};
  background: transparent;
  color: ${palette.slate};
  cursor: pointer;
  transition: color ${duration.fast} ${easing.standard},
    background-color ${duration.fast} ${easing.standard},
    box-shadow ${duration.base} ${easing.standard},
    transform ${duration.instant} ${easing.spring};

  & svg {
    width: ${spacing.md};
    height: ${spacing.md};
  }

  &:hover {
    background: ${palette.tealTint};
    color: ${palette.ink};
  }

  &:active {
    transform: scale(${SCALE.pressed});
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${touch} {
    width: ${layout.touchTarget};
    height: ${layout.touchTarget};
    margin: -${spacing.xs} -${spacing.xs} 0 0;
  }
`;

/* What happened when the button was pressed and push did not come on — one
   plain sentence, unfolding under the button rather than a toast that leaves. */
export const TipProblem = styled(InlineMessage)`
  margin-top: ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
`;
